import { mapLoginToSession, mapPartnerProfile } from "@/lib/auth-mappers";
import { api } from "@/lib/api/client";
import { partnerEndpoints } from "@/lib/api/endpoints";
import { ApiError } from "@/lib/api/errors";
import type {
  PartnerAuthUserDto,
  PartnerLoginDto,
  PartnerProfile,
  PartnerSession,
} from "@/types/partner";

export type LoginPayload = {
  email: string;
  password: string;
};

export type LoginResult =
  | { status: "authenticated"; session: PartnerSession }
  | { status: "needs_password_reset"; email: string; message: string };

export type PasswordResetRequest = {
  email: string;
};

export type PasswordResetVerify = {
  email: string;
  code: string;
  password: string;
};

type MessageResult = {
  message?: string;
};

function isNeedsPasswordReset(
  dto: PartnerLoginDto,
): dto is Extract<PartnerLoginDto, { needs_password_reset: true }> {
  return "needs_password_reset" in dto && dto.needs_password_reset === true;
}

export async function login(payload: LoginPayload): Promise<LoginResult> {
  const email = payload.email.trim().toLowerCase();

  const dto = await api.post<PartnerLoginDto>(partnerEndpoints.login, {
    email,
    password: payload.password,
  });

  if (isNeedsPasswordReset(dto)) {
    return {
      status: "needs_password_reset",
      email: (dto.email || email).trim().toLowerCase(),
      message:
        dto.message ?? "Password not set. Please reset your password to continue.",
    };
  }

  if (!dto?.access || !dto?.refresh || !dto?.user) {
    throw new ApiError("Unexpected login response from server", 500);
  }

  return { status: "authenticated", session: mapLoginToSession(dto) };
}

export async function fetchMe(): Promise<PartnerProfile> {
  const dto = await api.get<PartnerAuthUserDto>(partnerEndpoints.profile);
  return mapPartnerProfile(dto);
}

export async function requestPasswordReset(
  payload: PasswordResetRequest,
): Promise<string> {
  const dto = await api.post<MessageResult>(partnerEndpoints.resetPassword, {
    email: payload.email.trim().toLowerCase(),
  });
  return dto?.message ?? "Password reset code sent";
}

export async function verifyPasswordReset(
  payload: PasswordResetVerify,
): Promise<string> {
  const dto = await api.post<MessageResult>(partnerEndpoints.verifyPasswordReset, {
    email: payload.email.trim().toLowerCase(),
    code: payload.code.trim(),
    password: payload.password,
  });
  return dto?.message ?? "Password reset successfully.";
}
